import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import type { NotificationRecord } from '../services/notificationsApi'
import { LeadNotificationCard } from './LeadNotificationCard'
import { QualificationResultCard } from './QualificationResultCard'
import { hasBeenToasted, markToasted } from '../toastedNotifications'

interface NotificationTrayProps {
  notifications: NotificationRecord[]
  onDismiss: (id: string) => void
  onStartAction: (notification: NotificationRecord) => void
}

const MAX_VISIBLE = 3

/**
 * Renders the floating notification tray, top-right. Only unread
 * notifications are shown (max 3 at a time to avoid clutter), and only ones
 * not already toasted this browser (see toastedNotifications.ts) — otherwise
 * the same still-unread notification would pop again on every remount: every
 * navigation between Chat and Dashboard/Settings each mount their own Tray
 * instance, and every fresh login/reload starts fresh component state.
 *
 * Which ids are currently showing is tracked as this component's own state
 * (`activeIds`), not re-derived from `notifications` on every render. It has
 * to be: `notifications` re-fetches on a 5s poll independent of any card's
 * own 4s auto-dismiss countdown, so re-deriving "who's visible" straight from
 * that array on every poll could exclude a card mid-countdown the instant it
 * got marked toasted (which happens as soon as it's shown) — cutting its
 * countdown short at a random point instead of letting it run the full
 * duration. A card is only ever removed via its own onHidden callback, fired
 * when *it* decides it's done (auto-dismiss timer or manual close).
 *
 * A LEAD_ASSIGNED card offers "Start an Action"; a PROSPECT_QUALIFICATION_RESULT
 * card offers "Go to chat" (there's no lead-assignment context prompt that
 * applies to a qualification outcome).
 */
export const NotificationTray: React.FC<NotificationTrayProps> = ({
  notifications,
  onDismiss,
  onStartAction,
}) => {
  const [activeIds, setActiveIds] = useState<string[]>([])

  useEffect(() => {
    setActiveIds((prev) => {
      const room = MAX_VISIBLE - prev.length
      if (room <= 0) return prev

      const eligible = notifications.filter(
        (n) =>
          n.status === 'unread' &&
          (n.type === 'LEAD_ASSIGNED' || n.type === 'PROSPECT_QUALIFICATION_RESULT') &&
          !prev.includes(n.id) &&
          !hasBeenToasted(n.id),
      )
      if (eligible.length === 0) return prev

      const toAdd = eligible.slice(0, room)
      // Marked the instant a notification is chosen to display, not when its
      // countdown finishes — so even an immediate navigation away still
      // counts as "shown" and it won't toast again in a future mount.
      toAdd.forEach((n) => markToasted(n.id))
      return [...prev, ...toAdd.map((n) => n.id)]
    })
  }, [notifications])

  const handleHidden = (id: string) => {
    setActiveIds((prev) => prev.filter((activeId) => activeId !== id))
  }

  const visible = activeIds
    .map((id) => notifications.find((n) => n.id === id))
    .filter((n): n is NotificationRecord => !!n)

  if (visible.length === 0) return null

  return ReactDOM.createPortal(
    <div
      id="notification-tray"
      style={{
        position: 'fixed',
        top: '72px',
        right: '16px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        alignItems: 'flex-end',
        pointerEvents: 'none',
      }}
    >
      {visible.map((notification) => (
        <div
          key={notification.id}
          style={{
            pointerEvents: 'auto',
            animation: 'slideInRight 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          {notification.type === 'PROSPECT_QUALIFICATION_RESULT' ? (
            <QualificationResultCard
              notification={notification}
              onDismiss={onDismiss}
              autoDismissMs={4000}
              onHidden={() => handleHidden(notification.id)}
            />
          ) : (
            <LeadNotificationCard
              notification={notification}
              onDismiss={onDismiss}
              onStartAction={onStartAction}
              onHidden={() => handleHidden(notification.id)}
            />
          )}
        </div>
      ))}

      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
      `}</style>
    </div>,
    document.body,
  )
}
